// Session SC2C (scale run, spec gap 2): the ONE hook an office list uses to read a page the SERVER filtered, sorted and cut.
//
// A list that used to load every row and filter in the browser now asks for 50 at a time. The page owns WHAT is asked (its filters, search
// and sort, folded into one `queryKey` string and a `fetcher`); this hook owns the rest, the same way for every list:
//   - the first load shows the page's loader; a later change of filter or page keeps the rows on screen and dims them (`refreshing`),
//     so typing in a search box never throws the box away;
//   - a change of `queryKey` (a filter, a search, a sort) goes back to the first page; moving page keeps the query;
//   - an answer that arrives after a newer request was made is dropped (a slow reply never overwrites a fresh one);
//   - `reload()` reads the page the person is on again (after an action changed a row).
import { useCallback, useEffect, useRef, useState } from "react";

export interface ServerPage<R> {
  /** The latest answer, or null before the first one arrives. */
  data: R | null;
  /** True only until the first answer (or failure): show the loader. */
  loading: boolean;
  /** True while a later page or filter is being read; the previous rows stay on screen. */
  refreshing: boolean;
  error: boolean;
  offset: number;
  setOffset: (offset: number) => void;
  reload: () => Promise<void>;
}

export function useServerPage<R>(queryKey: string, fetcher: (offset: number) => Promise<R>, enabled = true): ServerPage<R> {
  const [pageState, setPageState] = useState<{ key: string; offset: number }>({ key: "", offset: 0 });
  const offset = pageState.key === queryKey ? pageState.offset : 0;
  const [data, setData] = useState<R | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const seq = useRef(0);
  const first = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const read = useCallback(async (at: number): Promise<boolean> => {
    const mine = ++seq.current;
    const res = await fetcherRef.current(at);
    if (mine !== seq.current) return false;
    setData(res);
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    if (first.current) setLoading(true);
    else setRefreshing(true);
    setError(false);
    read(offset)
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (!live) return;
        first.current = false;
        setLoading(false);
        setRefreshing(false);
      });
    return () => {
      live = false;
    };
  }, [queryKey, offset, enabled, read]);

  const setOffset = useCallback((o: number) => setPageState({ key: queryKey, offset: o }), [queryKey]);
  // A re-read after an action: the rows stay on screen while it runs; a failed read says so (the page offers Retry), as the lists always did.
  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      if (await read(offset)) setError(false);
    } catch {
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, [read, offset]);

  return { data, loading, refreshing, error, offset, setOffset, reload };
}
